import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { sendTestEmail } from '../../../services/emailService';
import { X, Save, Eye, AlertCircle, CheckCircle2, Info } from 'lucide-react';

const EmailTemplateManager = ({ isOpen, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  const [formData, setFormData] = useState({
    subject: '',
    html_content: '',
    template_name: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('email_templates')
        ?.select('*')
        ?.order('template_name');

      if (error) throw error;
      setTemplates(data || []);
      
      // Auto-select first template
      if (data && data?.length > 0) {
        selectTemplate(data?.[0]);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      showNotification('Fehler beim Laden der E-Mail-Vorlagen', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      subject: template?.subject || '',
      html_content: template?.html_content || '',
      template_name: template?.template_name || ''
    });
    setEditMode(false);
    setPreviewMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedTemplate?.id) return;

    try {
      setSaving(true);

      const { error } = await supabase
        ?.from('email_templates')
        ?.update({
          subject: formData?.subject,
          html_content: formData?.html_content,
          template_name: formData?.template_name,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', selectedTemplate?.id);

      if (error) throw error;

      showNotification('E-Mail-Vorlage erfolgreich aktualisiert', 'success');
      setEditMode(false);
      await fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      showNotification('Fehler beim Speichern der Vorlage', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    setIsSendingTest(true);
    setTestEmailResult(null);
    
    try {
      const result = await sendTestEmail('florian@baier.one');
      setTestEmailResult(result);
      
      // Auto-clear result after 5 seconds
      setTimeout(() => {
        setTestEmailResult(null);
      }, 5000);
    } catch (error) {
      console.error('Test email error:', error);
      setTestEmailResult({
        success: false,
        message: `Fehler: ${error?.message}`
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const renderPreview = () => {
    let previewHTML = formData?.html_content || '';
    
    // Replace common placeholders with example data for preview
    const placeholders = {
      dealershipName: 'Beispiel Partner GmbH',
      contactPerson: 'Max Mustermann',
      email: 'max@beispielpartner.de',
      customerName: 'Max Mustermann',
      customerEmail: 'max@beispielpartner.de',
      applicationId: 'APP-2025-001',
      applicationDate: new Date()?.toLocaleDateString('de-DE'),
      confirmationUrl: 'https://www.gridbalance.de/confirm-registration?token=example123',
      status: 'genehmigt',
      notes: 'Ihr Antrag wurde erfolgreich bearbeitet.',
      message: 'Vielen Dank für Ihre Geduld.',
      voucherCode: 'THG-2025-XYZ123',
      amount: '350',
      vehicleInfo: 'Tesla Model 3, 2023',
      expiryDate: '31.12.2025'
    };

    Object.entries(placeholders)?.forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewHTML = previewHTML?.replace(regex, value);
    });

    // Handle conditional sections (basic implementation)
    previewHTML = previewHTML?.replace(/{{#if \w+}}/g, '');
    previewHTML = previewHTML?.replace(/{{\/if}}/g, '');

    return previewHTML;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-primary to-secondary">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-white">
                E-Mail-Vorlagen verwalten
              </h2>
              <p className="text-sm text-white/80">
                Passen Sie Ihre E-Mail-Benachrichtigungen an
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center gap-3 ${
            notification?.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {notification?.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
            )}
            <p className="text-sm font-medium">{notification?.message}</p>
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Lade Vorlagen...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex">
            {/* Template List Sidebar */}
            <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
                  Verfügbare Vorlagen
                </h3>
                <div className="space-y-2">
                  {templates?.map((template) => (
                    <button
                      key={template?.id}
                      onClick={() => selectTemplate(template)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        selectedTemplate?.id === template?.id
                          ? 'bg-primary text-white shadow-lg'
                          : 'bg-white hover:bg-gray-100 text-gray-800'
                      }`}
                    >
                      <div className="font-semibold mb-1">{template?.template_name}</div>
                      <div className={`text-sm ${
                        selectedTemplate?.id === template?.id ? 'text-white/80' : 'text-gray-500'
                      }`}>
                        {template?.description}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(template?.available_variables || [])?.map((variable) => (
                          <span
                            key={variable}
                            className={`text-xs px-2 py-1 rounded ${
                              selectedTemplate?.id === template?.id
                                ? 'bg-white/20 text-white' :'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Template Editor/Preview */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {selectedTemplate ? (
                <>
                  {/* Action Buttons */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditMode(!editMode);
                          setPreviewMode(false);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          editMode
                            ? 'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => {
                          setPreviewMode(!previewMode);
                          setEditMode(false);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          previewMode
                            ? 'bg-secondary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Eye className="h-4 w-4" />
                        Vorschau
                      </button>
                      <button
                        onClick={handleSendTestEmail}
                        disabled={isSendingTest}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {isSendingTest ? 'Sende...' : 'Test-E-Mail senden'}
                      </button>
                    </div>
                    {editMode && (
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2 bg-success text-white rounded-lg hover:bg-success/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Speichern...' : 'Speichern'}
                      </button>
                    )}
                    {testEmailResult && (
                      <div className={`ml-4 px-4 py-2 rounded-lg flex items-center gap-2 ${
                        testEmailResult?.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {testEmailResult?.success ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        <span className="text-sm font-medium">{testEmailResult?.message}</span>
                      </div>
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {editMode ? (
                      <div className="space-y-6">
                        {/* Template Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Vorlagenname
                          </label>
                          <input
                            type="text"
                            name="template_name"
                            value={formData?.template_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        {/* Subject */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Betreff
                          </label>
                          <input
                            type="text"
                            name="subject"
                            value={formData?.subject}
                            onChange={handleInputChange}
                            placeholder="E-Mail Betreff"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        {/* HTML Content */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            HTML-Inhalt
                          </label>
                          <textarea
                            name="html_content"
                            value={formData?.html_content}
                            onChange={handleInputChange}
                            rows={20}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                            placeholder="HTML-Code der E-Mail"
                          />
                        </div>

                        {/* Available Variables Info */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-blue-900 mb-2">
                                Verfügbare Platzhalter
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {(selectedTemplate?.available_variables || [])?.map((variable) => (
                                  <code
                                    key={variable}
                                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm font-mono"
                                  >
                                    {`{{${variable}}}`}
                                  </code>
                                ))}
                              </div>
                              <p className="text-sm text-blue-700 mt-3">
                                Verwenden Sie diese Platzhalter in Betreff oder HTML-Inhalt. Sie werden automatisch durch die entsprechenden Werte ersetzt.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : previewMode ? (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
                            <div className="text-sm text-gray-600 mb-1">Betreff:</div>
                            <div className="font-semibold text-gray-900">{formData?.subject}</div>
                          </div>
                          <div className="p-6">
                            <div
                              dangerouslySetInnerHTML={{ __html: renderPreview() }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {selectedTemplate?.template_name}
                          </h3>
                          <p className="text-gray-600">{selectedTemplate?.description}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">Betreff:</div>
                          <div className="text-gray-900">{selectedTemplate?.subject}</div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">Verfügbare Platzhalter:</div>
                          <div className="flex flex-wrap gap-2">
                            {(selectedTemplate?.available_variables || [])?.map((variable) => (
                              <code
                                key={variable}
                                className="px-3 py-1 bg-white text-gray-800 rounded border border-gray-300 text-sm font-mono"
                              >
                                {`{{${variable}}}`}
                              </code>
                            ))}
                          </div>
                        </div>

                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-4">
                            Klicken Sie auf "Bearbeiten", um diese Vorlage anzupassen
                          </p>
                          <button
                            onClick={() => setEditMode(true)}
                            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                          >
                            Vorlage bearbeiten
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-gray-500">Wählen Sie eine Vorlage aus der Liste</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplateManager;